import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DataIcon from '@mui/icons-material/Storage';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import Panel from '../ui/Panel';

const DATA_TYPES = [
  ['mixed', 'Mixed (All Categories)'],
  ['project', 'Project Work'],
  ['unplanned', 'Unplanned Work'],
  ['toil', 'Toil'],
  ['change', 'Change Requests'],
  ['learn', 'Learning/Research'],
];

const SAMPLE_FACTS = [
  'Pre-defined tickets across all categories',
  'Realistic time tracking and effort estimates',
  'Different statuses, priorities and assignees',
  'Representative date distribution for time-series analysis',
];

const COUNT_MARKS = [10, 50, 100, 200].map((value) => ({ value, label: String(value) }));

/** Generate or wipe demo ticket data. Destructive actions confirm in the parent. */
const SampleDataSection = ({
  options,
  onOptionChange,
  onSliderChange,
  onGenerate,
  onClear,
  sampleDataLoading,
  clearDataLoading,
  databaseAvailable,
}) => (
  <Panel
    title="Sample Data"
    subtitle="Load predefined sample ticket data for testing and demonstration purposes."
  >
    {!databaseAvailable && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Requires a database connection.
      </Alert>
    )}

    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Number of tickets
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={options.count}
            onChange={onSliderChange('count')}
            aria-label="Number of tickets"
            valueLabelDisplay="auto"
            step={10}
            marks={COUNT_MARKS}
            min={10}
            max={200}
            disabled={!databaseAvailable}
          />
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth size="small" disabled={!databaseAvailable}>
          <InputLabel id="data-type-label">Data Type</InputLabel>
          <Select
            labelId="data-type-label"
            id="dataType"
            name="dataType"
            value={options.dataType}
            label="Data Type"
            onChange={onOptionChange}
          >
            {DATA_TYPES.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    <Stack direction="row" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
      <Button
        variant="outlined"
        color="error"
        onClick={onClear}
        disabled={clearDataLoading || sampleDataLoading || !databaseAvailable}
        startIcon={clearDataLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
      >
        {clearDataLoading ? 'Clearing…' : 'Clear All Data'}
      </Button>
      <Button
        variant="contained"
        onClick={onGenerate}
        disabled={sampleDataLoading || clearDataLoading || !databaseAvailable}
        startIcon={sampleDataLoading ? <CircularProgress size={18} color="inherit" /> : <DataIcon />}
      >
        {sampleDataLoading ? 'Loading…' : 'Load Sample Data'}
      </Button>
    </Stack>

    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutlineIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" color="primary">
            About sample data
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary">
          Sample data exercises the application without requiring real Jira data. The dataset
          includes:
        </Typography>
        <List dense>
          {SAMPLE_FACTS.map((fact) => (
            <ListItem key={fact} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={fact} />
            </ListItem>
          ))}
        </List>
        <Alert severity="warning">
          Loading sample data or clearing data will replace any existing data in the system.
        </Alert>
      </AccordionDetails>
    </Accordion>
  </Panel>
);

export default SampleDataSection;
